import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMappedTranslation, useUser } from 'hooks'
import { Button } from 'oneui'
import useCountriesQuery from 'queries/use-countries-query'

import { AutomationTagIds } from '../../../assets/test/test-automation-tag-id'

interface HeaderMenuType {
  id: string
  title: string
  to: string
  display: boolean
  eventNumber: string
}

export default function Menu() {
  const theme = useTheme()
  const mappedTranslation = useMappedTranslation()
  const { user } = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: countriesData, isLoading: isCountryPolicyLoading, isError: isPolicyError } = useCountriesQuery()

  const isFamilySupportedCountry = !isPolicyError
    ? countriesData?.countries?.[user.countryCode]?.childAccount?.childAccountSupported ?? false
    : false

  const menuRef = useRef<any>(null)
  const refreshRate = (1 / 60) * 1000
  let checkMomentumInterval

  const internalState = useRef({
    isMouseDown: false,
    isDragging: false,
    initialMouse: 0,
    lastMouse: 0,
    scrollSpeed: 0,
    activeTab: ''
  })

  const callbackMomentum = () => {
    const minimumSpeedToTriggerMomentum = 0.05

    checkMomentumInterval = setInterval(() => {
      const lastScrollSpeed = internalState.current.scrollSpeed
      const newScrollSpeed = lastScrollSpeed * 0.95
      internalState.current.scrollSpeed = newScrollSpeed

      runScroll()

      if (Math.abs(newScrollSpeed) < minimumSpeedToTriggerMomentum || internalState.current.isMouseDown) {
        internalState.current.scrollSpeed = 0
        clearInterval(checkMomentumInterval)
      }
    }, refreshRate)

    internalState.current.isDragging = false
  }

  const runScroll = () => {
    const move = internalState.current.scrollSpeed * refreshRate
    const offsetX = menuRef.current?.scrollLeft + move
    menuRef.current.scrollLeft = offsetX
  }

  const onMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    internalState.current.isMouseDown = true
    internalState.current.lastMouse = e.clientX
    internalState.current.initialMouse = e.clientX
  }

  const onMouseMove = (e: React.MouseEvent) => {
    if (!internalState.current.isMouseDown) {
      return
    }

    e.preventDefault()

    const move = internalState.current.lastMouse - e.clientX
    internalState.current.lastMouse = e.clientX

    internalState.current.scrollSpeed = move / refreshRate
    internalState.current.isDragging = true

    runScroll()
  }

  const onMouseUp = (e: React.MouseEvent) => {
    const isDragging = internalState.current.isDragging

    const move = internalState.current.initialMouse - e.clientX

    const isMotionIntentional = Math.abs(move) > 10 // Ignore movement below 10px

    const isDraggingConfirmed = isDragging && isMotionIntentional

    internalState.current.isMouseDown = false
    internalState.current.lastMouse = 0

    if (isDraggingConfirmed) {
      callbackMomentum()
    }
  }

  const handleClick = async (e, menu: HeaderMenuType) => {
    internalState.current.activeTab = menu.to
    let prevTab = menuRef.current.querySelector('[active = "true"]')

    if (prevTab !== null) {
      prevTab.setAttribute('active', 'false')
    }

    if (menu.id !== 'myaccount') {
      e.target.setAttribute('active', 'true')
    }

    // ignore navigating to current menu
    if (menu.to === location.pathname) {
      return
    }

    navigate(menu.to)
  }

  useEffect(() => {
    let currentPage = location.pathname.split('/')[2]
    if (currentPage === undefined || currentPage === '') {
      currentPage = 'overview'
    }

    let prevTab = menuRef.current.querySelector('[active = "true"]')
    if (prevTab !== null) {
      prevTab.setAttribute('active', 'false')
    }

    let activeTab = menuRef.current.querySelector(`#${currentPage}`)
    if (activeTab !== null) {
      activeTab.setAttribute('active', 'true')
      internalState.current.activeTab = activeTab
    }
  }, [location, user])

  useEffect(() => {
    if (internalState.current.activeTab === '') return

    const position = menuRef.current.querySelector('[active = "true"]')?.offsetLeft
    menuRef.current.scroll({ left: position - 5, behavior: 'smooth' })
  }, [internalState.current.activeTab])

  const isFamilyTabEnabled = () => {
    return isFamilySupportedCountry
  }

  const menuItems = [
    { id: 'profile', title: mappedTranslation('menu.profile'), to: '/dashboard/profile', display: true, eventNumber: '999' },
    {
      id: 'family',
      title: mappedTranslation('menu.family'),
      to: '/dashboard/family',
      display: isFamilyTabEnabled(),
      eventNumber: '993'
    },
    { id: 'security', title: mappedTranslation('menu.security'), to: '/dashboard/security', display: true, eventNumber: '998' },
    { id: 'privacy', title: mappedTranslation('menu.privacy'), to: '/dashboard/privacy', display: true, eventNumber: '997' },
    { id: 'places', title: mappedTranslation('menu.places'), to: '/dashboard/places', display: true, eventNumber: '996' },
    { id: 'services', title: mappedTranslation('menu.services'), to: '/dashboard/services', display: true, eventNumber: '995' },
    { id: 'devices', title: mappedTranslation('menu.devices'), to: '/dashboard/devices', display: true, eventNumber: '994' }
  ]

  const renderMenuItem = (props) => {
    const { id, title, sx = {} } = props
    return (
      <Button
        key={id}
        id={id}
        variant='text'
        disableRipple
        sx={{
          display: 'inline-block',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '18px',
          color: theme.palette.mode === 'light' ? '#010101' : '#f2f2f2',
          minWidth: 'max-content',
          padding: { xs: '15px 24px  ', md: '19px 32px' },
          height: { xs: '48px', md: '56px' },
          '&:hover': {
            background: 'inherit'
          },
          '&::before': {
            display: 'block',
            height: 0,
            fontWeight: 'bold',
            content: `"${title}"`,
            overflow: 'hidden'
          },
          '&[active = "true"]': {
            fontWeight: 'bold',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              left: '24px',
              bottom: 0,
              width: 'calc(100% - 48px)',
              height: '2px',
              backgroundColor: theme.palette.mode === 'light' ? '#222' : '#fff'
            }
          },
          '&:focus-visible': {
            borderRadius: 0,
            outlineOffset: '-2px'
          },
          ...sx
        }}
        data-testid={AutomationTagIds['button.menu-tab'] + '-' + id}
        onClick={(event) => handleClick(event, props)}
      >
        {title}
      </Button>
    )
  }

  return (
    <>
      <Box
        sx={{
          height: { xs: '104px', md: '56px' },
          bgcolor: theme.palette.mode === 'light' ? '#ffffff' : null,
          top: 0,
          position: {
            xs: 'sticky',
            md: 'inherit'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            maxWidth: '1440px',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '0 auto'
          }}
        >
          <Grid
            container
            direction={{ xs: 'column', md: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{ width: '100%' }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                width: { xs: '100%', md: '10%' },
                alignItems: 'end',
                height: '56px',
                borderBottom: { xs: '1px solid #ebebeb', md: 'inherit' }
              }}
              data-testid={AutomationTagIds['button.myaccount']}
            >
              {renderMenuItem({
                id: 'myaccount',
                title: mappedTranslation('menu.myaccount'),
                to: '/dashboard',
                disableRipple: true,
                sx: {
                  fontSize: '16px',
                  fontWeight: '700',
                  lineHeight: '22px',
                  px: '24px',
                  fontFamily: 'SamsungSharpSans',
                  padding: '17px 24px',
                  height: '56px'
                }
              })}
            </Grid>
            <Grid item sx={{ width: { xs: '100%', md: '90%' }, height: { xs: '48px', md: '56px' } }}>
              <Box
                ref={menuRef}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseUp}
                sx={{
                  display: 'flex',
                  overflowX: 'scroll',
                  scrollbarWidth: 'none',
                  px: '5px',
                  justifyContent: { xs: 'start', md: 'flex-end' },
                  '&::-webkit-scrollbar': { display: 'none' }
                }}
                data-testid={AutomationTagIds['parent-box.alltab']}
              >
                {renderMenuItem({
                  id: 'overview',
                  title: mappedTranslation('menu.overview'),
                  to: '/dashboard',
                  sx: { display: { xs: 'inline-block', md: 'none' } }
                })}
                {!isCountryPolicyLoading &&
                  menuItems.map((item) => {
                    return item.display && renderMenuItem(item)
                  })}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box
        id='header-shadow'
        sx={{
          width: '100%',
          opacity: '0.05',
          height: '32px',
          background: 'linear-gradient(180deg, #000000 0%, rgba(255,255,255,0) 100%)',
          backgroundBlendMode: 'normal'
        }}
      ></Box>
    </>
  )
}
